/**
 * Módulo Gestión Despacho — Fase 1 shell visual (mock, sin backend).
 */
import { Navigate, useSearchParams } from "react-router-dom";
import PanelGeneral from "@/pages/PanelGeneral";
import Clientes from "@/pages/Clientes";
import Ayuda from "@/pages/Ayuda";
import CompradorDespachoPage from "@/pages/despacho/CompradorDespachoPage";
import ClientesDespacho from "@/pages/despacho/ClientesDespacho";
import ClienteMaestroDespacho from "@/pages/despacho/ClienteMaestroDespacho";
import TareasDespacho from "@/pages/despacho/TareasDespacho";
import CalendarioDespacho from "@/pages/despacho/CalendarioDespacho";
import HonorariosDespacho from "@/pages/despacho/HonorariosDespacho";
import CentroMensajesDespacho from "@/pages/despacho/CentroMensajesDespacho";
import ReportesDespacho from "@/pages/despacho/ReportesDespacho";
import TaxSummaryCapturePage from "@/pages/despacho/TaxSummaryCapturePage";
import ConfiguracionDespacho from "@/pages/despacho/ConfiguracionDespacho";
import { CONFIGURACION_DESPACHO_MODULO_MOCK_PATH } from "@/modules/config/configuracionRoutes";
import Configuracion from "@/pages/Configuracion";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";
import ModuleHubInicio from "@/pages/modulos/ModuleHubInicio";
import DatosMaestrosHub from "@/pages/DatosMaestrosHub";
import CommercialClassificationNotice from "@/components/commercial/CommercialClassificationNotice";
import { buildModuloFromTabs, createModuloTab } from "@/modules/moduloTabRegistry";
import {
  DESPACHO_CONTEXT_LABEL,
  MODULO_DESPACHO_CARD,
  MODULO_DESPACHO_ID,
} from "@/modules/despacho/manifest";
import { isDespachoModulePath } from "@/modules/despacho/despachoService";
import { PANEL_GENERAL_EQUIPO_TAB } from "@/modules/panelGeneralTabs";
import { buildClientesDeepLink } from "@/lib/clientesDeepLink";
import { useAuth } from "@/lib/auth";
import { filterNavWithoutDespachoHonorarios } from "@/lib/teamRolePermissions";

function RedirectInformacionFiscalToClientes() {
  const [searchParams] = useSearchParams();
  const clientId = String(searchParams.get("client") || "").trim();
  const to = clientId ? buildClientesDeepLink({ clientId, tab: "obligaciones" }) : "/clientes";
  return <Navigate to={to} replace />;
}

function DespachoModuloInicio() {
  const { user } = useAuth();
  return (
    <ModuleHubInicio
      moduleId={MODULO_DESPACHO_ID}
      title={DESPACHO_CONTEXT_LABEL}
      subtitle="Usuarios internos, clientes fiscales y acceso rápido. Pendientes: Tablero operativo."
      getHubCards={() =>
        filterNavWithoutDespachoHonorarios(MODULO_DESPACHO.getHubCards(), user)
      }
    >
      <CommercialClassificationNotice variant="buyer_internal" />
      <CommercialClassificationNotice variant="fiscal_vs_operational" className="mt-3" />
    </ModuleHubInicio>
  );
}

const DESPACHO_TABS = [
  createModuloTab({
    index: true,
    path: "/",
    label: "Panel General",
    iconKey: "panel",
    end: true,
    showInNav: false,
    testid: "nav-panel-general",
    component: <PanelGeneral />,
  }),
  createModuloTab({
    path: "/despacho/inicio",
    label: "Inicio del módulo",
    description: "Tarjetas de acceso y 4 KPIs; el detalle de pendientes está en Tablero operativo.",
    iconKey: "panel",
    testid: "nav-despacho-inicio",
    component: <DespachoModuloInicio />,
  }),
  createModuloTab({
    path: "/despacho/comprador",
    label: "Límites de licencia",
    description: "Usuarios, clientes y módulos permitidos según el plan contratado (vista comercial).",
    iconKey: "panel",
    showInNav: false,
    navSection: "Permisos y límites",
    testid: "nav-despacho-limites-licencia",
    component: <CompradorDespachoPage />,
  }),
  createModuloTab({
    path: CONFIGURACION_DESPACHO_PATH,
    label: "Licencia del espacio",
    description:
      "Instalación de licencia y titular del espacio comprador. Para usuarios auxiliares operativos use Panel General → Actividades de Equipo.",
    iconKey: "settings",
    showInNav: false,
    navSection: "Equipo interno del comprador",
    testid: "nav-despacho-configuracion-espacio",
    component: <Configuracion entryMode="despacho" />,
  }),
  createModuloTab({
    path: "/datos-maestros",
    label: "Datos Maestros",
    description:
      "Catálogo fiscal compartido (RFC, contacto, FIEL). Punto de entrada a empresas y utilidades SAT.",
    iconKey: "database",
    navSection: "Clientes del comprador",
    testid: "nav-despacho-datos-maestros",
    hubCard: false,
    component: <DatosMaestrosHub />,
  }),
  createModuloTab({
    path: "/clientes",
    label: "Clientes",
    description:
      "Contribuyentes fiscales compartidos (RFC, contacto, FIEL). Mismo catálogo que Visor, Laboral y Trámites.",
    iconKey: "clients",
    navSection: "Clientes del comprador",
    hubOnlyNav: true,
    testid: "nav-despacho-clientes-fiscales",
    component: <Clientes />,
  }),
  createModuloTab({
    path: "/despacho/cliente-maestro",
    label: "Cliente Maestro",
    description:
      "Legacy — use /clientes. Redirige al catálogo fiscal unificado; el maestro se sincroniza al guardar.",
    iconKey: "clients",
    navSection: "Clientes del comprador",
    showInNav: false,
    testid: "nav-despacho-cliente-maestro",
    component: <ClienteMaestroDespacho />,
  }),
  createModuloTab({
    path: "/despacho/auxiliares",
    label: "Alta de Auxiliares en Despacho",
    description:
      "Administra el equipo interno del despacho comprador. Estos usuarios pueden operar módulos y clientes asignados según su rol y permisos. No son usuarios BNT ni clientes operativos.",
    iconKey: "users",
    navSection: "Equipo interno del comprador",
    showInNav: false,
    hubCard: false,
    testid: "nav-despacho-usuarios",
    component: <Navigate to={PANEL_GENERAL_EQUIPO_TAB} replace />,
  }),
  createModuloTab({
    path: "/despacho/clientes",
    label: "Asignacion de Empresas a Auxiliares",
    description:
      "Asigne qué auxiliar atiende cada empresa del catálogo fiscal (Clientes). Los WhatsApp y contactos se capturan en Clientes, no aquí.",
    iconKey: "clients",
    navSection: "Equipo interno del comprador",
    testid: "nav-despacho-clientes",
    component: <ClientesDespacho />,
  }),
  createModuloTab({
    path: "/despacho/informacion-fiscal",
    label: "Informacion fiscal empresas",
    description:
      "Redirige a Clientes → Obligaciones (CFS y expediente). No se muestra en el hub.",
    iconKey: "clients",
    navSection: "Equipo interno del comprador",
    showInNav: false,
    hubCard: false,
    testid: "nav-despacho-informacion-fiscal",
    component: <RedirectInformacionFiscalToClientes />,
  }),
  createModuloTab({
    path: "/despacho/tareas",
    label: "Tareas Operativas",
    description:
      "Seguimiento de obligaciones, tareas del equipo y pendientes por auxiliar.",
    iconKey: "listchecks",
    navSection: "Operación diaria",
    testid: "nav-despacho-tareas",
    component: <TareasDespacho />,
  }),
  createModuloTab({
    path: "/despacho/calendario",
    label: "Calendario y Recordatorios",
    description:
      "Reglas de vencimiento, plantillas e interruptores de avisos. La bandeja de conversación está en Atención a clientes.",
    iconKey: "calendar",
    navSection: "Operación diaria",
    testid: "nav-despacho-calendario",
    component: <CalendarioDespacho />,
  }),
  createModuloTab({
    path: "/despacho/honorarios",
    label: "Control de honorarios y Contratos",
    description:
      "Cobranza de honorarios, convenios y comprobantes ligados a CFDI.",
    iconKey: "reports",
    navSection: "Operación diaria",
    testid: "nav-despacho-honorarios",
    component: <HonorariosDespacho />,
  }),
  createModuloTab({
    path: "/despacho/mensajes",
    label: "Atención a clientes",
    description:
      "Bandeja de conversación por cliente (canal manual). Reglas y plantillas de avisos: Calendario.",
    iconKey: "message",
    navSection: "Operación avanzada",
    showInNav: false,
    hubCard: false,
    testid: "nav-despacho-atencion-clientes",
    component: <CentroMensajesDespacho />,
  }),
  createModuloTab({
    path: "/despacho/reportes",
    label: "Tablero operativo",
    description:
      "Pendientes por empresa: vencidos, esta semana, tareas y clientes incompletos (CFS / obligaciones).",
    iconKey: "reports",
    navSection: "Operación diaria",
    testid: "nav-despacho-reportes",
    component: <ReportesDespacho />,
  }),
  createModuloTab({
    path: "/despacho/resumen-impuestos",
    label: "Resumen Impuestos",
    description:
      "Captura anual ISR/IVA/retenciones por cliente, prellenado desde CFDI y exportación Excel.",
    iconKey: "reports",
    navSection: "Operación diaria",
    showInNav: false,
    hubCard: false,
    testid: "nav-despacho-resumen-impuestos",
    component: <TaxSummaryCapturePage />,
  }),
  createModuloTab({
    path: CONFIGURACION_DESPACHO_MODULO_MOCK_PATH,
    label: "Permisos y accesos",
    description:
      "Control de acceso por rol, módulo, cliente y responsabilidad (parámetros mock Fase 1).",
    iconKey: "settings",
    navSection: "Permisos y límites",
    designerOnly: true,
    testid: "nav-despacho-permisos",
    component: <ConfiguracionDespacho />,
  }),
  createModuloTab({
    path: "/ayuda",
    label: "Ayuda y Manuales",
    iconKey: "help",
    hubOnlyNav: true,
    showInNav: false,
    component: <Ayuda />,
  }),
];

export const MODULO_DESPACHO = buildModuloFromTabs({
  id: MODULO_DESPACHO_ID,
  card: MODULO_DESPACHO_CARD,
  contextLabel: DESPACHO_CONTEXT_LABEL,
  tabs: DESPACHO_TABS,
});

export function isDespachoCrossModulePath(pathname) {
  return isDespachoModulePath(pathname);
}

export const getModuloDespachoNavItems = () => MODULO_DESPACHO.getNavItems();
export const getModuloDespachoPaths = () => MODULO_DESPACHO.getPaths();
export const getModuloDespachoDefaultRoute = () => MODULO_DESPACHO.getDefaultRoute();
export const isModuloDespachoPath = (pathname) => MODULO_DESPACHO.isPathAllowed(pathname);
export const getModuloDespachoContextLabel = () => MODULO_DESPACHO.getContextLabel();
export const getModuloDespachoRoutes = () => MODULO_DESPACHO.getRoutes();
